import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as fabric from 'fabric';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Slider } from '@/components/ui/slider';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { toast } from 'sonner';
import { Type, Square, Circle, Image as ImageIcon, Upload, Wand2, Layers as LayersIcon, Palette, Trash2, Undo2, Redo2, Save, ShoppingCart, ArrowLeft, Plus, Minus, ArrowUp, ArrowDown, Sparkles, Star, Heart, SlidersHorizontal } from 'lucide-react';
import axios from 'axios';
import api, { assetUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { useLanguage } from '@/context/LanguageContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';

const BG_PRESETS = ['#0A0E27', '#FFFFFF', '#F87171', '#FBBF24', '#34D399', '#60A5FA', '#A78BFA', '#F472B6', '#0F172A'];
const COLORS = ['#FFFFFF', '#000000', '#F87171', '#FB923C', '#FBBF24', '#34D399', '#60A5FA', '#A78BFA', '#F472B6', '#E6248A', '#FF6B35'];

// PropertiesPanel is hoisted out of DesignEditor so it is a STABLE component
// type between renders. If defined inline it would be re-created on every
// parent re-render, forcing Radix <Tabs> — and every <Input> — to remount on
// each keystroke, which was the root cause of the "can't type in the AI
// prompt / design-name resets tabs" bug.
const PropertiesPanel = ({
  mobile = false,
  selected, layers, variant, product, aiPrompt, aiLoading,
  setBg, setFill, setOpacity, bringForward, sendBackward,
  setVariant, setAiPrompt, aiGenerate,
}) => (
  <Tabs defaultValue="properties" className="flex-1 flex flex-col min-h-0">
    <TabsList className="bg-transparent border-b rounded-none w-full justify-start px-2 h-11" style={{ borderColor: 'var(--jw-border)' }}>
      <TabsTrigger value="properties" data-testid={`editor-tab-properties${mobile ? '-mobile' : ''}`}>Properties</TabsTrigger>
      <TabsTrigger value="layers" data-testid={`editor-tab-layers${mobile ? '-mobile' : ''}`}>Layers</TabsTrigger>
      <TabsTrigger value="ai" data-testid={`editor-tab-ai${mobile ? '-mobile' : ''}`}>AI</TabsTrigger>
    </TabsList>
    <TabsContent value="properties" className="p-4 space-y-5 overflow-auto jw-scrollbar">
      <div>
        <p className="text-xs mb-2" style={{ color: 'var(--jw-text-muted)' }}>Background</p>
        <div className="grid grid-cols-9 gap-1.5">
          {BG_PRESETS.map(c => (
            <button
              key={c}
              onClick={() => setBg(c)}
              data-testid={`editor-bg-${c}-button${mobile ? '-mobile' : ''}`}
              aria-label={`Background ${c}`}
              className="jw-swatch"
              style={{ background: c }}
            />
          ))}
        </div>
      </div>
      {selected ? (
        <>
          <div>
            <p className="text-xs mb-2" style={{ color: 'var(--jw-text-muted)' }}>Fill</p>
            <div className="grid grid-cols-11 gap-1.5">
              {COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setFill(c)}
                  data-testid={`editor-fill-${c}-button${mobile ? '-mobile' : ''}`}
                  aria-label={`Fill ${c}`}
                  className="jw-swatch jw-swatch-sm"
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs mb-2" style={{ color: 'var(--jw-text-muted)' }}>Opacity</p>
            <Slider defaultValue={[Math.round((selected.opacity ?? 1) * 100)]} min={10} max={100} step={5} onValueChange={(v) => setOpacity(v[0] / 100)} data-testid={`editor-opacity-slider${mobile ? '-mobile' : ''}`} />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={bringForward} data-testid={`editor-forward-button${mobile ? '-mobile' : ''}`} className="jw-btn-gloss-ghost flex-1"><ArrowUp className="w-4 h-4 mr-1" /> Forward</Button>
            <Button size="sm" onClick={sendBackward} data-testid={`editor-backward-button${mobile ? '-mobile' : ''}`} className="jw-btn-gloss-ghost flex-1"><ArrowDown className="w-4 h-4 mr-1" /> Backward</Button>
          </div>
        </>
      ) : (
        <p className="text-sm" style={{ color: 'var(--jw-text-subtle)' }}>Select an element to edit its properties.</p>
      )}
      {product && (
        <div className="pt-4 border-t" style={{ borderColor: 'var(--jw-border)' }}>
          <p className="text-xs mb-2" style={{ color: 'var(--jw-text-muted)' }}>Finish</p>
          <div className="flex gap-2 flex-wrap">
            {product.variants?.map(v => (
              <button
                key={v}
                onClick={() => setVariant(v)}
                data-testid={`editor-variant-${v.replace(/\s+/g,'-').toLowerCase()}-button${mobile ? '-mobile' : ''}`}
                data-selected={variant === v ? 'true' : 'false'}
                className="jw-chip"
              >{v}</button>
            ))}
          </div>
        </div>
      )}
    </TabsContent>
    <TabsContent value="layers" className="p-2 overflow-auto jw-scrollbar">
      {layers.length === 0 && <p className="text-sm p-3" style={{ color: 'var(--jw-text-subtle)' }}>No layers yet.</p>}
      <div className="space-y-1">
        {layers.slice().reverse().map((l) => (
          <div key={l.id} className="flex items-center gap-2 p-2 rounded-md" style={{ color: 'var(--jw-text-muted)' }}>
            <LayersIcon className="w-4 h-4" />
            <span className="text-sm capitalize">{l.name}</span>
            <span className="ml-auto text-[10px] uppercase" style={{ color: 'var(--jw-text-subtle)' }}>{l.type}</span>
          </div>
        ))}
      </div>
    </TabsContent>
    <TabsContent value="ai" className="p-4 space-y-3 overflow-auto jw-scrollbar">
      <p className="text-sm" style={{ color: 'var(--jw-text-muted)' }}>Generate an image and place it on the canvas.</p>
      <Input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} data-testid={`editor-ai-prompt-input${mobile ? '-mobile' : ''}`} placeholder="e.g. cute avocado sticker" />
      <Button onClick={aiGenerate} disabled={aiLoading} data-testid={`editor-ai-generate-button${mobile ? '-mobile' : ''}`} className="jw-btn-glossy w-full rounded-md h-10">
        {aiLoading ? <><Sparkles className="w-4 h-4 mr-2 animate-pulse" /> Generating...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate</>}
      </Button>
    </TabsContent>
  </Tabs>
);

const DesignEditor = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const canvasElRef = useRef(null);
  const canvasRef = useRef(null);
  const historyRef = useRef({ undo: [], redo: [], suspended: false });
  const [product, setProduct] = useState(null);
  const [variant, setVariant] = useState(null);
  const [layers, setLayers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [designName, setDesignName] = useState('My design');
  const [saving, setSaving] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const { user } = useAuth();
  const { add } = useCart();
  const { t } = useLanguage();
  const { settings } = useSiteSettings();
  const storeOpen = settings.store_open !== false;

  useEffect(() => {
    api.get(`/products/${productId}`).then(({ data }) => {
      setProduct(data);
      setVariant(data.variants?.[0] || null);
    });
  }, [productId]);

  // Init canvas
  useEffect(() => {
    if (!canvasElRef.current || canvasRef.current) return;
    // Compute a size that fits the viewport (mobile-friendly)
    const container = canvasElRef.current.parentElement?.parentElement;
    const maxAvail = Math.min(
      (container?.clientWidth || window.innerWidth) - 32,
      window.innerHeight - 220
    );
    const initSize = Math.max(280, Math.min(600, maxAvail));
    const c = new fabric.Canvas(canvasElRef.current, {
      backgroundColor: '#ffffff',
      preserveObjectStacking: true,
      width: initSize,
      height: initSize,
    });
    canvasRef.current = c;

    // Handle resize (mobile rotate / window change)
    const handleResize = () => {
      const cont = canvasElRef.current?.parentElement?.parentElement;
      if (!cont) return;
      const avail = Math.min(cont.clientWidth - 32, window.innerHeight - 220);
      const target = Math.max(280, Math.min(600, avail));
      if (Math.abs((c.getWidth() || 0) - target) > 4) {
        const ratio = target / (c.getWidth() || target);
        c.setDimensions({ width: target, height: target });
        c.getObjects().forEach(o => {
          o.scaleX = (o.scaleX || 1) * ratio;
          o.scaleY = (o.scaleY || 1) * ratio;
          o.left = (o.left || 0) * ratio;
          o.top = (o.top || 0) * ratio;
          o.setCoords();
        });
        c.requestRenderAll();
      }
    };
    window.addEventListener('resize', handleResize);

    const refreshLayers = () => {
      const objs = c.getObjects();
      setLayers(objs.map((o, i) => ({ id: o.__id || (o.__id = `l-${Date.now()}-${i}`), type: o.type, name: o.__name || o.type })));
    };

    const pushHistory = () => {
      if (historyRef.current.suspended) return;
      historyRef.current.undo.push(JSON.stringify(c.toJSON(['__id', '__name'])));
      historyRef.current.redo = [];
      refreshLayers();
    };

    c.on('object:added', pushHistory);
    c.on('object:modified', pushHistory);
    c.on('object:removed', pushHistory);
    c.on('selection:created', (e) => setSelected(e.selected?.[0] || null));
    c.on('selection:updated', (e) => setSelected(e.selected?.[0] || null));
    c.on('selection:cleared', () => setSelected(null));
    refreshLayers();
    // initial snapshot
    historyRef.current.undo.push(JSON.stringify(c.toJSON(['__id', '__name'])));

    // apply prefill from library / AI — must survive React.StrictMode's double-
    // invoke of this effect. Do NOT remove the localStorage entry until the
    // fabric image is actually added, otherwise the disposed first-mount steals
    // the prefill and the live second-mount ends up with an empty canvas.
    const prefillRaw = localStorage.getItem('jw_editor_prefill');
    if (prefillRaw) {
      let prefillData = null;
      try {
        prefillData = JSON.parse(prefillRaw);
      } catch (err) {
        console.error('DesignEditor: prefill parse failed', err);
        localStorage.removeItem('jw_editor_prefill');
      }
      if (prefillData && prefillData.image_url) {
        (async () => {
          try {
            const url = prefillData.image_url;
            const absolute = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
            const res = await axios.get(absolute, { responseType: 'blob' });
            const dataUrl = await new Promise((resolve, reject) => {
              const fr = new FileReader();
              fr.onload = () => resolve(fr.result);
              fr.onerror = reject;
              fr.readAsDataURL(res.data);
            });
            const img = await fabric.FabricImage.fromURL(dataUrl);
            if (canvasRef.current !== c) return;
            const maxW = 320;
            if (img.width > maxW) img.scaleToWidth(maxW);
            img.set({ left: 100, top: 100 });
            img.__name = 'Prefill image';
            c.add(img);
            c.setActiveObject(img);
            c.requestRenderAll();
          } catch (err) {
            console.error('DesignEditor: prefill image load failed', err);
            if (canvasRef.current === c) toast.error('Could not load design into editor');
          } finally {
            localStorage.removeItem('jw_editor_prefill');
          }
        })();
      }
    }

    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo(); else undo();
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (document.activeElement && ['INPUT', 'TEXTAREA'].includes(document.activeElement.tagName)) return;
        deleteSelected();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', handleResize);
      c.dispose();
      canvasRef.current = null;
    };
    // eslint-disable-next-line
  }, []);

  const undo = () => {
    const c = canvasRef.current; if (!c) return;
    const h = historyRef.current;
    if (h.undo.length <= 1) return;
    const current = h.undo.pop();
    h.redo.push(current);
    const prev = h.undo[h.undo.length - 1];
    h.suspended = true;
    c.loadFromJSON(JSON.parse(prev), () => { c.renderAll(); h.suspended = false; });
  };
  const redo = () => {
    const c = canvasRef.current; if (!c) return;
    const h = historyRef.current;
    if (h.redo.length === 0) return;
    const next = h.redo.pop();
    h.undo.push(next);
    h.suspended = true;
    c.loadFromJSON(JSON.parse(next), () => { c.renderAll(); h.suspended = false; });
  };

  const addText = () => {
    const c = canvasRef.current; if (!c) return;
    const t = new fabric.IText('Your Text', { left: 120, top: 120, fill: '#111111', fontFamily: 'Space Grotesk', fontSize: 42 });
    t.__name = 'Text';
    c.add(t); c.setActiveObject(t); c.requestRenderAll();
  };
  const addRect = () => {
    const c = canvasRef.current; if (!c) return;
    const r = new fabric.Rect({ left: 140, top: 140, width: 140, height: 100, fill: '#F472B6', rx: 8, ry: 8 });
    r.__name = 'Rectangle';
    c.add(r); c.setActiveObject(r); c.requestRenderAll();
  };
  const addCircle = () => {
    const c = canvasRef.current; if (!c) return;
    const cc = new fabric.Circle({ left: 160, top: 160, radius: 60, fill: '#FBBF24' });
    cc.__name = 'Circle';
    c.add(cc); c.setActiveObject(cc); c.requestRenderAll();
  };
  const addStar = () => {
    const c = canvasRef.current; if (!c) return;
    // 5-point star via polygon
    const points = [];
    const cx = 0, cy = 0, outer = 60, inner = 25;
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? outer : inner;
      const a = (Math.PI / 5) * i - Math.PI / 2;
      points.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
    }
    const poly = new fabric.Polygon(points, { left: 180, top: 180, fill: '#FF6B35' });
    poly.__name = 'Star';
    c.add(poly); c.setActiveObject(poly); c.requestRenderAll();
  };
  const addHeart = () => {
    const c = canvasRef.current; if (!c) return;
    const heart = new fabric.Path('M 275 40 C 165 -60 -50 90 275 380 C 600 90 385 -60 275 40 Z', { fill: '#E6248A' });
    heart.scale(0.25); heart.set({ left: 200, top: 200 });
    heart.__name = 'Heart';
    c.add(heart); c.setActiveObject(heart); c.requestRenderAll();
  };

  // Fetches a remote image via a raw axios GET (no auth header needed for
  // public /api/uploads/*) and converts to a data URL. Loading via data-URL
  // guarantees the canvas won't be tainted, so `toDataURL()` on Save works
  // even when the image host doesn't return CORS headers — this was the
  // production RCA for "AI-generated icons can't be saved".
  const _urlToDataUrl = async (url) => {
    if (url.startsWith('data:')) return url;
    const absolute = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    const res = await axios.get(absolute, { responseType: 'blob' });
    return await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(fr.result);
      fr.onerror = reject;
      fr.readAsDataURL(res.data);
    });
  };

  const addImageFromUrl = async (url) => {
    const c = canvasRef.current; if (!c) return;
    try {
      const dataUrl = await _urlToDataUrl(url);
      const img = await fabric.FabricImage.fromURL(dataUrl);
      const maxW = 320;
      if (img.width > maxW) img.scaleToWidth(maxW);
      img.set({ left: 100, top: 100 });
      img.__name = 'Image';
      c.add(img); c.setActiveObject(img); c.requestRenderAll();
    } catch (err) {
      console.error('addImageFromUrl failed', err);
      toast.error('Could not load image');
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await api.post('/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      addImageFromUrl(assetUrl(data.url));
      toast.success('Image uploaded');
    } catch (e) { toast.error('Upload failed'); }
    e.target.value = '';
  };

  const aiGenerate = async () => {
    if (!aiPrompt.trim()) { toast('Enter a prompt'); return; }
    setAiLoading(true);
    try {
      const category = product?.type || 'sticker';
      const { data } = await api.post('/ai/generate-design', { prompt: aiPrompt, category });
      addImageFromUrl(assetUrl(data.image_url));
      toast.success('AI design added to canvas');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed');
    } finally { setAiLoading(false); }
  };

  const setBg = (color) => {
    const c = canvasRef.current; if (!c) return;
    c.backgroundColor = color;
    c.requestRenderAll();
    // push history
    historyRef.current.undo.push(JSON.stringify(c.toJSON(['__id', '__name'])));
  };

  const deleteSelected = () => {
    const c = canvasRef.current; if (!c) return;
    const objs = c.getActiveObjects();
    objs.forEach(o => c.remove(o));
    c.discardActiveObject(); c.requestRenderAll();
  };

  const bringForward = () => { const c = canvasRef.current; const o = c?.getActiveObject(); if (o) { c.bringObjectForward(o); c.requestRenderAll(); } };
  const sendBackward = () => { const c = canvasRef.current; const o = c?.getActiveObject(); if (o) { c.sendObjectBackwards(o); c.requestRenderAll(); } };

  const setFill = (color) => {
    const c = canvasRef.current; const o = c?.getActiveObject(); if (!o) return;
    o.set('fill', color); c.requestRenderAll();
    historyRef.current.undo.push(JSON.stringify(c.toJSON(['__id', '__name'])));
  };

  const setOpacity = (v) => {
    const c = canvasRef.current; const o = c?.getActiveObject(); if (!o) return;
    o.set('opacity', v); c.requestRenderAll();
  };

  const saveDesign = async () => {
    if (!user) { toast('Please sign in first'); navigate('/login'); return; }
    const c = canvasRef.current; if (!c) return;
    setSaving(true);
    try {
      const canvasJson = c.toJSON(['__id', '__name']);
      const preview = c.toDataURL({ format: 'png', multiplier: 1 });
      const { data } = await api.post('/user-designs', {
        name: designName,
        product_type: product?.type || 'sticker',
        canvas_json: canvasJson,
        preview_base64: preview,
      });
      toast.success('Design saved');
      return data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const saveAndAddToCart = async () => {
    if (!storeOpen) {
      toast.error(t('closure.cannotAdd', "We're paused for new orders — you can browse but can't add to cart right now."));
      return;
    }
    const design = await saveDesign();
    if (!design) return;
    try {
      await add({ product_id: product.id, variant, user_design_id: design.id, quantity: 1 });
      toast.success(t('designDetail.addedToast', 'Added to cart'));
      navigate('/cart');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || t('designDetail.couldNotAdd', 'Add to cart failed'));
    }
  };

  const PANEL_PROPS = {
    selected, layers, variant, product, aiPrompt, aiLoading,
    setBg, setFill, setOpacity, bringForward, sendBackward,
    setVariant, setAiPrompt, aiGenerate,
  };

  const TOOLS = [
    { icon: Type, label: 'Text', onClick: addText, tid: 'editor-tool-text-button' },
    { icon: Square, label: 'Rect', onClick: addRect, tid: 'editor-tool-rect-button' },
    { icon: Circle, label: 'Circle', onClick: addCircle, tid: 'editor-tool-circle-button' },
    { icon: Star, label: 'Star', onClick: addStar, tid: 'editor-tool-star-button' },
    { icon: Heart, label: 'Heart', onClick: addHeart, tid: 'editor-tool-heart-button' },
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] pb-16 md:pb-0">
      {/* Topbar */}
      <div className="h-14 border-b flex items-center px-2 sm:px-3 gap-1 sm:gap-2 shrink-0" style={{ borderColor: 'var(--jw-border)', background: 'var(--jw-bg-alt)' }}>
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="editor-back-button"><ArrowLeft className="w-4 h-4" /></Button>
        <Input value={designName} onChange={(e) => setDesignName(e.target.value)} data-testid="editor-name-input" className="flex-1 sm:flex-none sm:w-56 h-9" />
        <div className="ml-1 text-xs hidden lg:block" style={{ color: 'var(--jw-text-muted)' }}>Designing: <b>{product?.name || '...'}</b></div>
        <div className="hidden sm:block flex-1" />
        <Button variant="ghost" size="icon" onClick={undo} data-testid="editor-undo-button"><Undo2 className="w-4 h-4" /></Button>
        <Button variant="ghost" size="icon" onClick={redo} data-testid="editor-redo-button"><Redo2 className="w-4 h-4" /></Button>
        <div className="hidden sm:block w-px h-6 mx-1" style={{ background: 'var(--jw-border)' }} />
        {/* Mobile: settings sheet trigger */}
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="editor-mobile-settings-button" className="md:hidden"><SlidersHorizontal className="w-4 h-4" /></Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[70vh] p-0 flex flex-col">
            <PropertiesPanel mobile {...PANEL_PROPS} />
          </SheetContent>
        </Sheet>
        <Button onClick={saveDesign} disabled={saving} data-testid="editor-save-button" size="sm" className="jw-btn-gloss-ghost h-9 hidden sm:inline-flex"><Save className="w-4 h-4 mr-1" /> {t('editor.save', 'Save')}</Button>
        <Button onClick={saveAndAddToCart} disabled={!storeOpen} data-testid="editor-add-to-cart-button" size="sm" className="jw-btn-glossy h-9 hidden sm:inline-flex disabled:opacity-50 disabled:cursor-not-allowed"><ShoppingCart className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">{storeOpen ? t('editor.addToCart', 'Add to Cart') : t('closure.addToCartLabel', 'Orders paused')}</span></Button>
      </div>

      {/* Body: on mobile — vertical stack: canvas + tool bar; on md+ — three columns */}
      <div className="flex-1 flex flex-col md:grid md:grid-cols-[64px_1fr_320px] min-h-0">
        {/* Left rail (desktop) */}
        <div className="hidden md:flex border-r p-2 flex-col gap-1" style={{ borderColor: 'var(--jw-border)', background: 'var(--jw-bg-alt)' }}>
          {TOOLS.map(t => (
            <button key={t.label} onClick={t.onClick} data-testid={t.tid} title={t.label} className="w-12 h-12 rounded-lg grid place-items-center hover:opacity-80" style={{ color: 'var(--jw-text-muted)' }}>
              <t.icon className="w-5 h-5" />
            </button>
          ))}
          <label className="w-12 h-12 rounded-lg grid place-items-center hover:opacity-80 cursor-pointer" style={{ color: 'var(--jw-text-muted)' }} data-testid="editor-tool-upload-button" title="Upload image">
            <Upload className="w-5 h-5" />
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
          </label>
          <button onClick={deleteSelected} data-testid="editor-tool-delete-button" title="Delete" className="w-12 h-12 rounded-lg grid place-items-center text-rose-400 hover:opacity-80">
            <Trash2 className="w-5 h-5" />
          </button>
        </div>

        {/* Canvas */}
        <div className="flex-1 p-3 sm:p-6 grid place-items-center overflow-auto" style={{ background: 'var(--jw-bg-elev)', backgroundImage: 'var(--jw-checker)', backgroundSize: '24px 24px' }}>
          <div className="jw-card p-2 sm:p-3 max-w-full">
            <canvas ref={canvasElRef} data-testid="editor-canvas" />
          </div>
        </div>

        {/* Mobile: horizontal tool bar at bottom */}
        <div className="md:hidden border-t flex items-center gap-1 overflow-x-auto jw-scrollbar px-2 py-2 shrink-0" style={{ borderColor: 'var(--jw-border)', background: 'var(--jw-bg-alt)' }}>
          {TOOLS.map(t => (
            <button key={t.label} onClick={t.onClick} data-testid={`${t.tid}-mobile`} title={t.label} className="shrink-0 w-11 h-11 rounded-lg grid place-items-center jw-panel" style={{ color: 'var(--jw-text)' }}>
              <t.icon className="w-5 h-5" />
            </button>
          ))}
          <label className="shrink-0 w-11 h-11 rounded-lg grid place-items-center jw-panel cursor-pointer" data-testid="editor-tool-upload-button-mobile" title="Upload">
            <Upload className="w-5 h-5" />
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
          </label>
          <button onClick={deleteSelected} data-testid="editor-tool-delete-button-mobile" title="Delete" className="shrink-0 w-11 h-11 rounded-lg grid place-items-center jw-panel text-rose-400">
            <Trash2 className="w-5 h-5" />
          </button>
        </div>

        {/* Right panel (desktop) */}
        <div className="hidden md:flex border-l flex-col" style={{ borderColor: 'var(--jw-border)', background: 'var(--jw-bg-alt)' }}>
          <PropertiesPanel {...PANEL_PROPS} />
        </div>
      </div>

      {/* Sticky mobile action bar — persistent Save + Add to Cart across the
        * bottom on small screens so users never lose sight of the primary CTAs
        * while designing. Hidden on md+ where the topbar has room for them. */}
      <div
        className="md:hidden fixed bottom-0 inset-x-0 z-40 border-t flex items-center gap-2 px-3 py-2 shrink-0"
        style={{
          borderColor: 'var(--jw-border)',
          background: 'var(--jw-bg-alt)',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 8px)',
        }}
        data-testid="editor-mobile-action-bar"
      >
        <Button
          onClick={saveDesign}
          disabled={saving}
          data-testid="editor-save-button-mobile"
          className="jw-btn-gloss-ghost flex-1 h-12 text-sm"
        >
          <Save className="w-4 h-4 mr-2" /> {saving ? t('editor.saving', 'Saving...') : t('editor.saveDesign', 'Save design')}
        </Button>
        <Button
          onClick={saveAndAddToCart}
          disabled={!storeOpen}
          data-testid="editor-add-to-cart-button-mobile"
          className="jw-btn-glossy flex-[1.3] h-12 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <ShoppingCart className="w-4 h-4 mr-2" /> {storeOpen ? t('editor.addToCart', 'Add to Cart') : t('closure.addToCartLabel', 'Orders paused')}
        </Button>
      </div>
    </div>
  );
};

export default DesignEditor;
